/**
 * ExpirationBanner Component
 * Displays expiration information for Kit de Atendimento
 *
 * @version 2025.1.0
 */

import React, { useMemo } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import { differenceInDays, format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ExpirationBannerProps {
  expiresAt: string;
}

export const ExpirationBanner: React.FC<ExpirationBannerProps> = ({ expiresAt }) => {
  const { severity, daysLeft, formattedDate } = useMemo(() => {
    const expirationDate = parseISO(expiresAt);
    const now = new Date();
    const days = differenceInDays(expirationDate, now);
    const formatted = format(expirationDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });

    let sev: 'info' | 'warning' | 'error' = 'info';
    if (days <= 3) {
      sev = 'error';
    } else if (days <= 7) {
      sev = 'warning';
    }

    return {
      severity: sev,
      daysLeft: days,
      formattedDate: formatted,
    };
  }, [expiresAt]);

  const getMessage = () => {
    if (daysLeft <= 0) {
      return 'Este link expirou.';
    }
    if (daysLeft === 1) {
      return 'Este link expira amanhã.';
    }
    if (daysLeft <= 3) {
      return `Este link expira em ${daysLeft} dias.`;
    }
    if (daysLeft <= 7) {
      return `Este link expira em ${formattedDate} (${daysLeft} dias restantes).`;
    }
    return `Este link é válido até ${formattedDate}.`;
  };

  return (
    <Alert severity={severity} sx={{ mb: 3 }}>
      <AlertTitle>Validade do Link</AlertTitle>
      {getMessage()}
    </Alert>
  );
};

export default ExpirationBanner;
